/**
 * @fileoverview Notification Center Component - Bell icon with dropdown
 *
 * Displays a bell icon with unread count badge and dropdown panel
 * for viewing persistent notifications.
 */

import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotificationCenter } from '../context/NotificationCenterContext';
import { FiBell, FiCheck, FiX, FiTrash2 } from 'react-icons/fi'; // eslint-disable-line no-unused-vars
import '../styles/NotificationCenter.css';
import NotificationItem from './NotificationItem';
import NotificationInboxFull from './NotificationInboxFull';

const NotificationCenter = () => {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    isLoading,
    isOpen,
    isFullInboxOpen,
    markAllAsRead,
    dismissAll,
    toggleOpen,
    close,
    openFullInbox,
    closeFullInbox
  } = useNotificationCenter();

  const dropdownRef = useRef(null);
  const bellButtonRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      // Ignore clicks on the bell button (toggle handles that)
      if (bellButtonRef.current && bellButtonRef.current.contains(event.target)) {
        return;
      }
      // Close if click is outside the dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        close();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, close]);

  // Group notifications by date
  const groupedNotifications = notifications.reduce((groups, notification) => {
    const date = new Date(notification.created_at);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let groupKey;
    if (date.toDateString() === today.toDateString()) {
      groupKey = 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      groupKey = 'Yesterday';
    } else {
      groupKey = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }

    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(notification);
    return groups;
  }, {});

  return (
    <div className="notification-center" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        ref={bellButtonRef}
        className="notification-bell-button"
        onClick={toggleOpen}
        aria-label="Notifications"
      >
        <FiBell size={20} />
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Dropdown */}
      {isOpen && (
        <div className="notification-dropdown">
          {/* Header */}
          <div className="notification-dropdown-header">
            <h3>Notifications</h3>
            <div className="notification-header-actions">
              {unreadCount > 0 && (
                <button
                  className="notification-action-btn"
                  onClick={markAllAsRead}
                  title="Mark all as read"
                >
                  <FiCheck size={16} />
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  className="notification-action-btn"
                  onClick={() => {
                    if (window.confirm('Dismiss all notifications?')) {
                      dismissAll();
                    }
                  }}
                  title="Dismiss all"
                >
                  <FiTrash2 size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Notification List */}
          <div className="notification-list">
            {isLoading ? (
              <div className="notification-loading">
                <div className="spinner" />
                <p>Loading notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">
                <FiBell size={48} />
                <p>No notifications</p>
                <span>You're all caught up!</span>
              </div>
            ) : (
              Object.entries(groupedNotifications).map(([dateGroup, groupNotifications]) => (
                <div key={dateGroup} className="notification-group">
                  <div className="notification-group-header">{dateGroup}</div>
                  {groupNotifications.map(notification => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                    />
                  ))}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="notification-dropdown-footer">
              <button
                className="notification-view-all-btn"
                onClick={openFullInbox}
              >
                View All Notifications
              </button>
            </div>
          )}
        </div>
      )}

      {/* Full Inbox Modal */}
      <NotificationInboxFull
        isOpen={isFullInboxOpen}
        onClose={closeFullInbox}
        onNavigate={(url) => {
          closeFullInbox();
          navigate(url);
        }}
      />
    </div>
  );
};

export default NotificationCenter;
